import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import BackLink from '@artemis/components/BackLink';
import Metadata from '@artemis/components/Metadata';
import NavBar from '@artemis/components/NavBar';
import PageContainer from '@artemis/components/PageContainer';
import AccountControls from '@artemis/containers/AccountControls';
import {
  FormattedMessage,
  useFormatMessage,
} from '@artemis/integrations/contentful/utils';
import {
  createTeam,
  joinTeam,
  loadTeamById,
  loadVerificationStatus,
  sendVerificationEmail,
} from '@artemis/store/teams/slice';
import useAuthWithLogin from '@artemis/utils/useAuthWithLogin';
import {
  BACK_URL_PARAM,
  JOIN_TEAM_SUCCESS_PARAM,
  VERIFICATION_SOURCE_TYPES,
} from '@artemis/utils/query/constants';
import usePersistedQuery from '@artemis/utils/query/usePersistedQuery';
import { getOrderSourceSubdomain } from '@artemis/store/order/selectors';
import {
  getIsSendVerificationEmailLoading,
  getIsVerificationStatusLoading,
  getSendVerificationEmailErrorCode,
  getTeamName,
  getVerificationStatusTeamName,
} from '@artemis/store/teams/selectors';
import { addToast } from '@artemis/store/toasts/slice';
import Loading from '@artemis/components/Loading';
import {
  EMAIL_VERIFICATION_STATUS,
  TEAM_STATUS,
  VERIFICATION_ERROR_CODE,
} from '../constants';
import {
  logCheckInboxContinueClick,
  logCheckInboxEditClick,
  logCheckInboxResendClick,
  logCreateCompanyNextClick,
} from './analytics';
import CheckInbox from './CheckInbox';
import ConnectEmail from './ConnectEmail';
import SelectFloor from './SelectFloor';
import SelectLocation from './SelectLocation';
import {
  CONNECT_WORK_EMAIL,
  CHECK_INBOX,
  SELECT_LOCATION,
  SELECT_FLOOR,
  REQUEST_TO_JOIN,
  CREATE_TEAM,
} from './steps';
import CreateTeam from './CreateTeam';
import RequestToJoin from './RequestToJoin';
import useRedirectUserOnVerifiedTeam from './useRedirectUserOnVerifiedTeam';

const Container = styled(PageContainer)`
  max-width: 552px;
  padding: 24px;
  ${props => props.theme.isTablet`
    margin-top: 46px;
  `}
`;

const FlexPadding = styled.div`
  flex: 1;
`;

const formatPostSuccessUrl = (backUrl, teamName) =>
  queryString.stringifyUrl({
    url: `${backUrl}`,
    query: teamName ? { [JOIN_TEAM_SUCCESS_PARAM]: teamName } : {},
  });

const JoinTeamPage = () => {
  const dispatch = useDispatch();
  const persistedQuery = usePersistedQuery();
  const { query } = useRouter();
  const backUrlParam = query[BACK_URL_PARAM];

  const subdomain = useSelector(getOrderSourceSubdomain);
  const isSendVerificationEmailLoading = useSelector(
    getIsSendVerificationEmailLoading,
  );
  const sendEmailVerificationErrorCode = useSelector(
    getSendVerificationEmailErrorCode,
  );
  const isVerificationStatusLoading = useSelector(
    getIsVerificationStatusLoading,
  );
  const associatedTeamName = useSelector(getVerificationStatusTeamName);
  const teamName = useSelector(getTeamName);

  const [currentStep, setCurrentStep] = useState(CONNECT_WORK_EMAIL);
  const [email, setEmail] = useState('');
  const [teamId, setTeamId] = useState();
  const [locationId, setLocationId] = useState();

  const pageTitle = useFormatMessage({
    entry: 'teams.joinTeamPage.title',
  });

  const hasPreviousStep = currentStep !== CONNECT_WORK_EMAIL;

  const showBack = backUrlParam || hasPreviousStep;

  const backUrl = backUrlParam
    ? decodeURIComponent(backUrlParam)
    : `/order${persistedQuery}`;

  // Redirect user if they are already in a secured team with their email verified
  const { isReady } = useRedirectUserOnVerifiedTeam(
    formatPostSuccessUrl(backUrl),
  );

  const onBack = e => {
    e.preventDefault();

    switch (currentStep) {
      case SELECT_FLOOR:
        setCurrentStep(SELECT_LOCATION);
        return;
      case SELECT_LOCATION:
      case CREATE_TEAM:
      case CHECK_INBOX:
      case REQUEST_TO_JOIN:
        setCurrentStep(CONNECT_WORK_EMAIL);
        return;
      case CONNECT_WORK_EMAIL:
      default:
        if (backUrl) {
          window.location.assign(backUrl);
        }
    }
  };

  const { authenticated, initialized } = useAuthWithLogin({
    title: <FormattedMessage entry="login.title.continue" />,
    loginMessage: ' ',
  });

  // Go to appropriate step based on email verification status
  const handleEmailVerificationResult = result => {
    const { emailVerificationStatus, teamStatus, associatedTeamDetails } =
      result;

    const isVerified =
      emailVerificationStatus === EMAIL_VERIFICATION_STATUS.VERIFIED;

    if (!isVerified) {
      setCurrentStep(CHECK_INBOX);
      return;
    }

    if (teamStatus === TEAM_STATUS.JOINED) {
      window.location.assign(
        formatPostSuccessUrl(backUrl, associatedTeamDetails?.name),
      );
    } else if (teamStatus === TEAM_STATUS.INVITE_ONLY) {
      setCurrentStep(REQUEST_TO_JOIN);
    } else if (teamStatus === TEAM_STATUS.JOINABLE) {
      dispatch(loadTeamById(associatedTeamDetails.teamId));
      setTeamId(associatedTeamDetails.teamId);
      setCurrentStep(SELECT_LOCATION);
    } else if (teamStatus === TEAM_STATUS.DOES_NOT_EXIST) {
      setCurrentStep(CREATE_TEAM);
    }
  };

  const handleEmailVerificationError = error => {
    const errorCode = error?.response?.data?.errors?.[0]?.code;
    if (errorCode === VERIFICATION_ERROR_CODE.TEAM_INVITE_REQUIRED) {
      setCurrentStep(REQUEST_TO_JOIN);
    }
  };

  const onVerifyEmail = ({ workEmail, onSuccess }) => {
    setEmail(workEmail);

    const sourceType = subdomain
      ? VERIFICATION_SOURCE_TYPES.ORGANIZATION
      : VERIFICATION_SOURCE_TYPES.RITUAL_WEB;

    dispatch(
      sendVerificationEmail({
        sourceType,
        subdomain,
        email: workEmail,
        onSuccess: result => {
          if (onSuccess) {
            onSuccess(result);
          }
          handleEmailVerificationResult(result);
        },
        onError: handleEmailVerificationError,
      }),
    );
  };

  const showErrorToast = message => {
    dispatch(addToast({ toast: { error: { message } } }));
  };

  const showSuccessToast = message => {
    dispatch(addToast({ toast: { message } }));
  };

  const step = useMemo(() => {
    // Show loading state while waiting for user + team details to load
    if (!isReady) {
      return <Loading isLoading />;
    }

    switch (currentStep) {
      case CONNECT_WORK_EMAIL:
        return (
          <ConnectEmail
            onNext={workEmail => onVerifyEmail({ workEmail })}
            isLoading={isSendVerificationEmailLoading}
            errorCode={sendEmailVerificationErrorCode}
          />
        );
      case REQUEST_TO_JOIN:
        return (
          <RequestToJoin
            email={email}
            onBack={() => {
              window.location.assign(backUrl);
            }}
          />
        );
      case CHECK_INBOX:
        return (
          <CheckInbox
            teamName={associatedTeamName}
            email={email}
            isCheckStatusLoading={isVerificationStatusLoading}
            isResendLoading={isSendVerificationEmailLoading}
            onNext={() =>
              dispatch(
                loadVerificationStatus({
                  email,
                  onSuccess: result => {
                    const isVerified =
                      result.emailVerificationStatus ===
                      EMAIL_VERIFICATION_STATUS.VERIFIED;

                    logCheckInboxContinueClick({ success: isVerified, email });

                    if (!isVerified) {
                      showErrorToast('teams.checkInbox.checkInboxToVerify');
                    }

                    handleEmailVerificationResult(result);
                  },
                }),
              )
            }
            onEditEmail={() => {
              logCheckInboxEditClick({ email });
              setEmail('');
              setCurrentStep(CONNECT_WORK_EMAIL);
            }}
            onSendVerificationEmail={workEmail => {
              logCheckInboxResendClick({ email: workEmail });
              onVerifyEmail({
                workEmail,
                onSuccess: () =>
                  showSuccessToast('teams.checkInbox.resendSuccess'),
              });
            }}
          />
        );
      case CREATE_TEAM:
        return (
          <CreateTeam
            email={email}
            onNext={({ name, googlePlaceId }) => {
              logCreateCompanyNextClick({
                name,
                googlePlaceId,
                email,
              });
              dispatch(
                createTeam({
                  name,
                  googlePlaceId,
                  email,
                  onSuccess: ({ teamDetails, locations }) => {
                    setTeamId(teamDetails?.teamId);

                    // Automatically select location user entered (there may be more than 1 location in the response if the team already existed)
                    const matchingLocation = (locations || []).find(
                      location => location.googlePlaceId === googlePlaceId,
                    );

                    if (matchingLocation) {
                      setLocationId(matchingLocation.locationId);
                      setCurrentStep(SELECT_FLOOR);
                    } else {
                      setCurrentStep(SELECT_LOCATION);
                    }
                  },
                }),
              );
            }}
          />
        );
      case SELECT_LOCATION:
        return (
          <SelectLocation
            teamId={teamId}
            email={email}
            onNext={selectedLocation => {
              setLocationId(selectedLocation.locationId);
              setCurrentStep(SELECT_FLOOR);
            }}
          />
        );
      case SELECT_FLOOR:
        return (
          <SelectFloor
            teamId={teamId}
            locationId={locationId}
            onNext={({ floorId }) => {
              dispatch(
                joinTeam({
                  teamId,
                  locationId,
                  floorId,
                  onSuccess: () => {
                    window.location.assign(
                      formatPostSuccessUrl(backUrl, teamName),
                    );
                  },
                }),
              );
            }}
          />
        );
      default:
        return null;
    }
  }, [
    isReady,
    currentStep,
    setCurrentStep,
    isSendVerificationEmailLoading,
    sendEmailVerificationErrorCode,
    isVerificationStatusLoading,
  ]);

  return (
    <>
      <Metadata title={pageTitle} noindex nofollow />
      <NavBar>
        {showBack ? (
          <BackLink onClick={onBack}>
            <FormattedMessage entry="global.back" />
          </BackLink>
        ) : (
          <FlexPadding />
        )}
        <AccountControls />
      </NavBar>
      {initialized && authenticated && <Container>{step}</Container>}
    </>
  );
};

export default JoinTeamPage;
